/**
 * The geometry and the arithmetic behind a stamped brush.
 *
 * A soft brush is not a filled outline — it is a tip stamped repeatedly along
 * the stroke at a fixed spacing, each stamp a radial gradient. This module is
 * the part of that with no canvas in it: where the stamps go, and how much
 * paint each one carries.
 *
 * Split out because it is the half that can be tested. vitest runs in plain
 * node here with no DOM, and both of the defects this feature can ship are
 * arithmetic rather than pixels — a stroke that goes gappy when drawn quickly,
 * and one that turns grainy when drawn gently. See docs/decisions/soft-brush.md
 * for the measurements behind the numbers.
 */

export interface StampPoint {
  x: number;
  y: number;
  /** 0..1. Interpolated along a segment like position is. */
  pressure: number;
}

export interface Resampled {
  stamps: StampPoint[];
  /**
   * Distance still to travel before the next stamp is due.
   *
   * Fed back in as `pending` on the next call. This is what makes the stroke
   * independent of how the pointer events happened to arrive — see below.
   */
  pending: number;
}

/**
 * Walk a polyline, emitting a stamp every `spacing` pixels of arc length.
 *
 * **Chunk-invariant, and that is the whole point.** A stroke arrives as pointer
 * events, and how many arrive per frame depends on the device, the frame rate
 * and how fast the user is moving — none of which should change the picture.
 * Carrying `pending` across calls means resampling a path in one go and
 * resampling it in twenty pieces produce the same stamps, so drawing quickly and
 * drawing slowly deposit the same paint. Without it, every frame boundary would
 * restart the spacing and lay down a double stamp there: a stroke visibly beaded
 * at intervals that track the frame rate.
 *
 * The caller passes the previous batch's last point as the first element of the
 * next batch, so the segment across the boundary is walked exactly once.
 *
 * A stamp is emitted at `points[0]` when `pending` is zero or less, which is how
 * a stroke starts: a tap with no movement still leaves a mark.
 */
export function resample(points: readonly StampPoint[], spacing: number, pending = 0): Resampled {
  const stamps: StampPoint[] = [];
  if (points.length === 0) return { stamps, pending };
  // A non-positive spacing would emit stamps forever without advancing.
  if (!(spacing > 0)) return { stamps, pending: 0 };

  const first = points[0];
  let need = pending;
  if (need <= 0 && first) {
    stamps.push(first);
    need = spacing;
  }

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    // Duplicate points are ordinary — a held pointer emits them — and dividing
    // by this length below would produce NaN and poison every later stamp.
    if (length === 0) continue;

    let travelled = 0;
    while (length - travelled >= need) {
      travelled += need;
      const t = travelled / length;
      stamps.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
        pressure: a.pressure + (b.pressure - a.pressure) * t,
      });
      need = spacing;
    }
    need -= length - travelled;
  }

  return { stamps, pending: need };
}

/**
 * The smallest alpha the canvas can hold without visible quantisation.
 *
 * Canvas stores alpha in 8 bits. Below about four levels the rounding error on
 * a single stamp is several percent, and a stamp is composited tens of times
 * per brush width — the error patterns rather than averages out, and the stroke
 * comes out grainy. Measured at spacing 0.02 with a light flow: one level in
 * 255, a 12% error, fifty times over.
 */
export const MIN_ALPHA_LEVELS = 4;

/**
 * How much paint one stamp carries.
 *
 * `flow` is coverage accumulated over **one brush diameter of travel**, not per
 * stamp — which is the correction that makes the controls independent. Read
 * per-stamp, tightening spacing to smooth a stroke also darkens it, because the
 * deposited total goes as flow/spacing: measured, the same stroke went from
 * faint at spacing 0.25 to nearly black at 0.02. Then every control fights the
 * others.
 *
 * Inverting the compositing gives the per-stamp value directly. Over one
 * diameter there are `1/spacing` stamps, and n stamps of alpha a accumulate to
 * `1-(1-a)^n`, so setting that equal to flow and solving:
 *
 *     a = 1 - (1 - flow) ** spacing
 *
 * After this, spacing buys smoothness and nothing else.
 */
export function perStampAlpha(flow: number, spacing: number): number {
  const f = clamp01(flow);
  if (f >= 1) return 1;
  if (f <= 0) return 0;
  return 1 - (1 - f) ** Math.max(0, spacing);
}

/**
 * The tightest spacing this flow can afford.
 *
 * The catch in normalising is that per-stamp alpha shrinks as spacing tightens,
 * so a light flow at a tight spacing lands under the 8-bit floor and the stroke
 * goes grainy — precisely when it is used most gently, which is the shading
 * case. The answer is to widen the spacing rather than lie about the alpha:
 * fewer, better-represented stamps carry the same total paint.
 *
 * Solving `1-(1-flow)^s >= MIN_ALPHA_LEVELS/255` for s.
 */
export function spacingFor(flow: number, requested: number): number {
  const f = clamp01(flow);
  // Nothing to deposit, or every stamp is opaque: the floor cannot be missed.
  if (f <= 0 || f >= 1) return Math.max(0, requested);
  const floor = MIN_ALPHA_LEVELS / 255;
  // Both logs are negative, so the division comes out positive.
  const minimum = Math.log(1 - floor) / Math.log(1 - f);
  return Math.max(requested, minimum);
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * How much of the configured build-up one stamp carries, given pen pressure.
 *
 * **0.5 is neutral, not half.** A device with no sensor reports exactly 0.5 —
 * see the pointer handler in main.ts, which already normalises a missing
 * reading to it — so treating pressure as a plain multiplier would halve every
 * stroke drawn with a mouse or a finger. Scaling around 0.5 leaves those
 * untouched and gives a pen its range below.
 *
 * Clamped at 1, so pressure can only ever take away. Build-up is the ceiling
 * the user set; a firm press should reach it, not exceed it.
 */
export function pressureScale(pressure: number): number {
  if (!Number.isFinite(pressure)) return 1;
  if (pressure <= 0) return 0;
  return Math.min(1, pressure / 0.5);
}

/**
 * How much paint lands at a given distance from the brush centre.
 *
 * `t` is the distance as a fraction of the radius: 0 at the centre, 1 at the
 * rim. The profile is the radial gradient's, expressed as arithmetic so the
 * seam bleed can follow the same curve the canvas draws for the brush itself.
 *
 * That symmetry is the whole point. The bleed is a set of individual texels on
 * a neighbouring face, so it cannot be a gradient fill — it has to be shaded
 * one texel at a time, and it was not: every crossed texel took the same alpha
 * regardless of how far into the brush it was. A soft brush therefore laid a
 * flat, hard-edged slab across the fold, which is both the "fold hardens"
 * complaint and the reason reaching onto a neighbouring part read as painting
 * on it rather than wrapping around it.
 *
 * A hard brush keeps a hard bleed, which is correct: it matches its own edge.
 */
export function edgeFalloff(t: number, hardness: number): number {
  if (!Number.isFinite(t) || t <= 0) return 1;
  if (t >= 1) return 0;
  const h = Number.isFinite(hardness) ? Math.max(0, Math.min(1, hardness)) : 1;
  // Solid core out to the hardness stop, then a straight ramp to nothing.
  if (h >= 1) return 1;
  if (t <= h) return 1;
  return (1 - t) / (1 - h);
}

/**
 * The eight directions a constrained line may take.
 *
 * Horizontal, vertical and both diagonals. Eight rather than four because a
 * 45-degree run is what a sleeve seam and a collar edge actually are, and
 * rather than sixteen because the extra angles are not ones anyone reaches for
 * and they make the snap feel indecisive near the boundaries.
 */
const SNAP_DIRECTIONS = 8;

/**
 * Where a straight line from `a` should end, given where the pointer is.
 *
 * The direction snaps to the nearest eighth-turn; the length is the pointer's
 * **projection** onto that direction, not its raw distance. That difference is
 * what makes an axis-aligned line feel right: drag right and slightly up, and
 * the end lands under the cursor's x rather than overshooting it by the
 * hypotenuse. It is also what every editor does, so fingers already expect it.
 *
 * A drag that ends up behind the anchor returns the anchor itself, because a
 * negative projection would flip the line to the opposite direction — which
 * reads as the line snapping to a heading you did not ask for.
 */
export function snapToAngle(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): { x: number; y: number } {
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return { x: ax, y: ay };

  const step = (Math.PI * 2) / SNAP_DIRECTIONS;
  const snapped = Math.round(Math.atan2(dy, dx) / step) * step;
  const ux = Math.cos(snapped);
  const uy = Math.sin(snapped);

  // Projection onto the snapped ray, never behind it.
  const along = Math.max(0, dx * ux + dy * uy);
  return { x: ax + ux * along, y: ay + uy * along };
}
